/**
 * useMediaSession — Media Session API integration for TTS audio.
 *
 * Sets metadata (title, artist) and action handlers (play/pause) on the
 * system media notification / lock screen when TTS audio is playing.
 *
 * No-op on desktop / browsers without Media Session API support.
 *
 * AC: #1-#10 (Story 9.3)
 */

import { useEffect } from 'react'
import { useAudioStore } from '@/stores/audioStore'
import { audioService } from '@/services/audio'

export function useMediaSession(): void {
  const audioStatus = useAudioStore((state) => state.status)
  const whisperWords = useAudioStore((state) => state.whisperWords)
  const whisperComplete = useAudioStore((state) => state.whisperComplete)

  // Task 1: Metadata setup and cleanup (AC #1, #5, #8, #9)
  useEffect(() => {
    // AC #9: Feature detection
    if (!('mediaSession' in navigator)) return

    const isActive = audioStatus !== 'idle' && audioStatus !== 'error' && audioStatus !== 'skipped'

    if (isActive) {
      try {
        navigator.mediaSession.metadata = new MediaMetadata({
          title: 'GIERMEK.online — Narracja',
          artist: 'GIERMEK.online',
        })
      } catch (err) {
        console.warn('[useMediaSession] Failed to set metadata:', err)
      }
    } else {
      try {
        navigator.mediaSession.metadata = null
      } catch (err) {
        console.warn('[useMediaSession] Failed to clear metadata:', err)
      }
    }

    return () => {
      // AC #5: Cleanup on unmount — clear metadata so lock screen doesn't show stale info
      if (!('mediaSession' in navigator)) return
      try {
        navigator.mediaSession.metadata = null
      } catch (err) {
        console.warn('[useMediaSession] Failed to clear metadata on unmount:', err)
      }
    }
  }, [audioStatus])

  // Task 2: Action handlers — play/pause, explicit null for seekto/nexttrack/previoustrack (AC #4, #6, #7)
  useEffect(() => {
    // AC #9: Feature detection
    if (!('mediaSession' in navigator)) return

    try {
      navigator.mediaSession.setActionHandler('play', () => {
        audioService.resume()
      })
    } catch (err) {
      console.warn('[useMediaSession] Failed to set play handler:', err)
    }

    try {
      navigator.mediaSession.setActionHandler('pause', () => {
        audioService.pause()
      })
    } catch (err) {
      console.warn('[useMediaSession] Failed to set pause handler:', err)
    }

    // AC #6: No seekto — seek without prompter sync creates desync
    try {
      navigator.mediaSession.setActionHandler('seekto', null)
    } catch (err) {
      console.warn('[useMediaSession] Failed to clear seekto handler:', err)
    }

    // AC #7: Explicit null to prevent Android/iOS showing default buttons
    try {
      navigator.mediaSession.setActionHandler('nexttrack', null)
    } catch (err) {
      console.warn('[useMediaSession] Failed to clear nexttrack handler:', err)
    }

    try {
      navigator.mediaSession.setActionHandler('previoustrack', null)
    } catch (err) {
      console.warn('[useMediaSession] Failed to clear previoustrack handler:', err)
    }

    return () => {
      // Cleanup all handlers on unmount
      if (!('mediaSession' in navigator)) return
      const actions: MediaSessionAction[] = ['play', 'pause', 'seekto', 'nexttrack', 'previoustrack']
      for (const action of actions) {
        try {
          navigator.mediaSession.setActionHandler(action, null)
        } catch (err) {
          console.warn(`[useMediaSession] Failed to clear ${action} handler on unmount:`, err)
        }
      }
    }
  }, [])

  // Task 2.6: playbackState synchronization (AC #4)
  useEffect(() => {
    // AC #9: Feature detection
    if (!('mediaSession' in navigator)) return

    try {
      if (audioStatus === 'playing' || audioStatus === 'resumed') {
        navigator.mediaSession.playbackState = 'playing'
      } else if (audioStatus === 'paused' || audioStatus === 'skipped') {
        navigator.mediaSession.playbackState = 'paused'
      } else {
        // idle | loading | error
        navigator.mediaSession.playbackState = 'none'
      }
    } catch (err) {
      console.warn('[useMediaSession] Failed to set playbackState:', err)
    }
  }, [audioStatus])

  // Task 3: Position state via timeupdate listener (AC #2, #3, #9, #10)
  useEffect(() => {
    // AC #9: Feature detection
    if (!('mediaSession' in navigator)) return

    // Compute duration from whisper timestamps (only when complete)
    const duration =
      whisperComplete && whisperWords.length > 0
        ? whisperWords[whisperWords.length - 1].end
        : undefined

    if (audioStatus !== 'playing') {
      return
    }

    const audio = audioService.getElement()

    const handleTimeUpdate = () => {
      // Guard: no duration yet (whisper not arrived) — skip to avoid setPositionState(undefined)
      if (duration === undefined) return

      // AC #9: Safari <15.4 has mediaSession but not setPositionState
      if (typeof navigator.mediaSession?.setPositionState !== 'function') return

      try {
        navigator.mediaSession.setPositionState({
          duration,
          playbackRate: 1,
          position: audio.currentTime,
        })
      } catch (err) {
        console.warn('[useMediaSession] Failed to set position state:', err)
      }
    }

    audio.addEventListener('timeupdate', handleTimeUpdate)

    return () => {
      // AC #10: Must remove timeupdate listener on cleanup
      audio.removeEventListener('timeupdate', handleTimeUpdate)
    }
  }, [audioStatus, whisperComplete, whisperWords])
}
